import { useCallback, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { BaseButton } from '@repo/common';
import { searchTools } from '../../../api/pathfinder/pathfinder.api';
import { ReactComponent as SearchIcon } from '@repo/common/assets/icons/20px/icon_20px_search.svg';
import { ReactComponent as CancelIcon } from '@repo/common/assets/icons/16px/icon_16px_cancel.svg';
import { ReactComponent as PlusIcon } from '@repo/common/assets/icons/16px/icon_16px_plus_outline.svg';

/**
 * 기술 도구 다중 선택 검색 컴포넌트 (GitHub Topics API)
 *
 * value:    string[]  — 선택된 도구 이름 배열
 * onChange: (names: string[]) => void
 */
function PathfinderToolSearchSelect({
  value = [],
  onChange,
  placeholder = 'Search tools (e.g. Figma, Python, Excel)',
}) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const [showLeftFade, setShowLeftFade] = useState(false);

  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const chipsScrollRef = useRef(null);
  const debounceRef = useRef(null);
  const closeTimerRef = useRef(null);

  const openPanel = useCallback(() => {
    clearTimeout(closeTimerRef.current);
    setIsOpen(true);
    requestAnimationFrame(() => setVisible(true));
  }, []);

  const closePanel = useCallback(() => {
    setVisible(false);
    closeTimerRef.current = setTimeout(() => setIsOpen(false), 200);
  }, []);

  useEffect(() => {
    const handleMouseDown = (e) => {
      if (!rootRef.current?.contains(e.target)) closePanel();
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      clearTimeout(closeTimerRef.current);
      clearTimeout(debounceRef.current);
    };
  }, [closePanel]);

  // 칩 추가 시 스크롤을 맨 오른쪽으로
  useEffect(() => {
    const el = chipsScrollRef.current;
    if (!el) return;
    el.scrollLeft = el.scrollWidth;
  }, [value]);

  const handleScroll = useCallback(() => {
    const el = chipsScrollRef.current;
    if (!el) return;
    setShowLeftFade(el.scrollLeft > 4);
  }, []);

  const handleQueryChange = useCallback(
    (e) => {
      const q = e.target.value;
      setQuery(q);
      if (!isOpen) openPanel();
      clearTimeout(debounceRef.current);
      if (!q || q.trim().length < 1) {
        setSuggestions([]);
        return;
      }
      debounceRef.current = setTimeout(async () => {
        try {
          const results = await searchTools(q.trim());
          setSuggestions(results.map((t) => t.name));
        } catch {
          setSuggestions([]);
        }
      }, 300);
    },
    [isOpen, openPanel],
  );

  const handleAdd = useCallback(
    (name) => {
      if (!value.includes(name)) {
        onChange([...value, name]);
      }
      setQuery('');
      setSuggestions([]);
      inputRef.current?.focus();
    },
    [value, onChange],
  );

  const handleRemove = useCallback(
    (name) => {
      onChange(value.filter((v) => v !== name));
      openPanel();
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    },
    [value, onChange, openPanel],
  );

  const unselected = suggestions.filter((s) => !value.includes(s));
  const trimmedQuery = query.trim();
  const showAddAsSkill =
    trimmedQuery.length > 0 && !value.includes(trimmedQuery);
  const showPanel =
    isOpen && (value.length > 0 || unselected.length > 0 || showAddAsSkill);
  const isHighlighted = isOpen;
  const showClear = Boolean(query || value.length > 0);

  const handleClearQuery = useCallback(() => {
    setQuery('');
    setSuggestions([]);
    inputRef.current?.focus();
  }, []);

  return (
    <div ref={rootRef} className='flex w-full flex-col gap-[6px]'>
      {/* 입력창 */}
      <div className='flex h-[80px] w-full flex-col items-start justify-center'>
        <div
          className={twMerge(
            'group relative flex h-full w-full items-center rounded-[15px] bg-surface-lightgray60 pl-4 pr-3 py-[10px] border-2 transition-colors duration-200 overflow-hidden',
            isHighlighted ? 'border-surface-green40' : 'border-transparent',
          )}
          onMouseDown={(e) => {
            if (e.target.closest('button')) return;
            e.preventDefault();
            inputRef.current?.focus();
            openPanel();
          }}
        >
          <SearchIcon
            className={twMerge(
              'mr-2 size-5 shrink-0 transition-colors duration-200 group-hover:text-green-deep02',
              isHighlighted ? 'text-green-deep02' : 'text-text-placeholder',
            )}
          />

          {/* 선택된 칩 + 입력 (가로 스크롤) */}
          <div
            ref={chipsScrollRef}
            onScroll={handleScroll}
            className='flex min-w-0 flex-1 flex-nowrap items-center gap-2 overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
          >
            {value.map((name) => (
              <span
                key={name}
                className='inline-flex h-[34px] shrink-0 items-center gap-2 rounded-full bg-grayscale-gray07 pl-3 pr-2 py-2'
              >
                <span className='text-[12px] font-medium leading-[1.4] whitespace-nowrap text-grayscale-gray01'>
                  {name}
                </span>
                <BaseButton
                  type='text'
                  size='s'
                  htmlType='button'
                  onClick={() => handleRemove(name)}
                  className='size-4 min-w-0 shrink-0 rounded-none border-0 bg-transparent p-0 hover:bg-transparent'
                  icon={<CancelIcon className='size-4 text-grayscale-gray01' />}
                />
              </span>
            ))}
            <input
              ref={inputRef}
              value={query}
              onChange={handleQueryChange}
              onFocus={() => openPanel()}
              placeholder={value.length > 0 ? '' : placeholder}
              className='min-w-[80px] flex-1 shrink-0 border-none bg-transparent p-0 text-base text-text-primary outline-none placeholder:text-text-placeholder'
            />
          </div>

          {/* 왼쪽 페이드 그라디언트 — 스크롤 시 표시 */}
          {showLeftFade && (
            <div
              aria-hidden
              className='pointer-events-none absolute left-[52px] top-0 h-full w-8'
              style={{
                background:
                  'linear-gradient(to right, rgba(236,233,228,0.96), transparent)',
              }}
            />
          )}

          {showClear && (
            <BaseButton
              type='text'
              size='s'
              htmlType='button'
              onClick={handleClearQuery}
              className='ml-2 size-5 min-w-0 shrink-0 rounded-full border-0 p-0 bg-grayscale-gray02 text-text-placeholder transition-colors hover:bg-grayscale-gray03 hover:text-text-placeholder'
              aria-label='Clear search text'
              icon={<CancelIcon className='size-4' />}
            />
          )}
        </div>
      </div>

      {/* 드롭다운 패널 */}
      {showPanel && (
        <div
          className={twMerge(
            'w-full rounded-[15px] border border-grayscale-gray01 bg-grayscale-gray00 px-6 pb-6 pt-5 shadow-3',
            'transition-all duration-200 ease-out origin-top',
            visible
              ? 'opacity-100 scale-y-100 translate-y-0'
              : 'opacity-0 scale-y-95 -translate-y-1',
          )}
        >
          <div className='flex w-full flex-wrap gap-x-3 gap-y-2'>
            {/* 이미 선택된 칩 (어두운 배경, × 버튼) */}
            {value.map((name) => (
              <BaseButton
                key={name}
                type='text'
                size='m'
                htmlType='button'
                typo={false}
                isStatic
                gap='gap-2'
                iconPosition='after'
                icon={<CancelIcon className='size-4 text-grayscale-gray01' />}
                onClick={() => handleRemove(name)}
                textWrapperClass='h-auto flex-none text-[12px] font-medium leading-[1.4] whitespace-nowrap text-grayscale-gray01'
                className='h-[34px] min-w-0 cursor-pointer items-center gap-2 rounded-full border-0 bg-grayscale-gray07 pl-3 pr-2 py-2'
              >
                {name}
              </BaseButton>
            ))}

            {/* 구분선 */}
            {value.length > 0 && unselected.length > 0 && (
              <div className='my-1 h-px w-full bg-grayscale-gray01' />
            )}

            {/* 검색 결과 칩 (밝은 배경, + 버튼) */}
            {unselected.map((name) => (
              <BaseButton
                key={name}
                type='text'
                size='m'
                htmlType='button'
                typo={false}
                isStatic
                gap='gap-2'
                iconPosition='after'
                icon={<PlusIcon className='size-4 text-grayscale-gray07' />}
                onClick={() => handleAdd(name)}
                textWrapperClass='h-auto flex-none text-[12px] font-medium leading-[1.4] whitespace-nowrap text-grayscale-gray07'
                className='h-[34px] min-w-0 cursor-pointer items-center gap-2 rounded-full border-0 bg-grayscale-gray01 pl-3 pr-2 py-2 hover:bg-grayscale-gray02 transition-colors duration-150'
              >
                {name}
              </BaseButton>
            ))}

            {/* "Add as skill" 옵션 */}
            {showAddAsSkill && (
              <>
                {(value.length > 0 || unselected.length > 0) && (
                  <div className='my-1 h-px w-full bg-grayscale-gray01' />
                )}
                <BaseButton
                  type='text'
                  size='m'
                  htmlType='button'
                  typo={false}
                  isStatic
                  gap='gap-2'
                  iconPosition='after'
                  icon={<PlusIcon className='size-4 text-grayscale-gray07' />}
                  onClick={() => handleAdd(trimmedQuery)}
                  textWrapperClass='h-auto flex-none text-[12px] font-medium leading-[1.4] whitespace-nowrap text-grayscale-gray07'
                  className='h-[34px] min-w-0 cursor-pointer items-center gap-2 rounded-full border-0 bg-grayscale-gray01 pl-3 pr-2 py-2 hover:bg-grayscale-gray02 transition-colors duration-150'
                >
                  Add &ldquo;{trimmedQuery}&rdquo; as a skill
                </BaseButton>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default PathfinderToolSearchSelect;
